import apiClient from './ApiClient';
import { getRestaurantId } from '../utils/JwtUtil';

/**
 * Dashboard API service for integrating with backend dashboard endpoints
 */

/**
 * Get dashboard overview with key metrics
 * @returns {Promise} Dashboard overview data
 */
export const getDashboardOverview = async () => {
  const restaurantId = getRestaurantId();
  const response = await apiClient.get('/dashboard/overview', {
    params: { restaurantId }
  });
  return response.data;
};

/**
 * Get top performing meals
 * @param {number} limit - Number of top meals to retrieve (default: 10)
 * @returns {Promise} List of top performing meals
 */
export const getTopPerformingMeals = async (limit = 10) => {
  const restaurantId = getRestaurantId();
  const response = await apiClient.get('/dashboard/meal-performance', {
    params: { 
      restaurantId,
      limit
    }
  });
  return response.data;
};

/**
 * Get subscription metrics and analytics
 * @returns {Promise} Subscription metrics data
 */
export const getSubscriptionMetrics = async () => {
  const restaurantId = getRestaurantId();
  const response = await apiClient.get('/dashboard/subscription-metrics', {
    params: { restaurantId }
  });
  return response.data;
};

/**
 * Get today's meal availability status
 * @returns {Promise} Today's meal availability data
 */
export const getTodayAvailability = async () => {
  const restaurantId = getRestaurantId();
  const response = await apiClient.get('/dashboard/today-availability', {
    params: { restaurantId }
  });
  return response.data;
};

/**
 * Get operational alerts and notifications
 * @returns {Promise} Operational alerts data
 */
export const getOperationalAlerts = async () => {
  const restaurantId = getRestaurantId();
  const response = await apiClient.get('/dashboard/operational-alerts', {
    params: { restaurantId }
  });
  return response.data;
};

/**
 * Get recent activity feed
 * @param {number} limit - Number of activity items to retrieve (default: 20)
 * @returns {Promise} Recent activity data
 */
export const getRecentActivity = async (limit = 20) => {
  const restaurantId = getRestaurantId();
  const response = await apiClient.get('/dashboard/recent-activity', {
    params: { 
      restaurantId,
      limit
    }
  });
  return response.data;
};

/**
 * Get revenue statistics for specified number of days
 * @param {number} days - Number of days to get revenue stats for (default: 7)
 * @returns {Promise} Revenue statistics data
 */
export const getRevenueStats = async (days = 7) => {
  const restaurantId = getRestaurantId();
  const response = await apiClient.get('/dashboard/revenue-stats', {
    params: { 
      restaurantId,
      days
    }
  });
  return response.data;
};

/**
 * Get all dashboard data in a single call for dashboard page initialization
 * @returns {Promise} Object containing all dashboard data
 */
export const getAllDashboardData = async () => {
  try {
    const [
      overview,
      topMeals,
      subscriptionMetrics,
      todayAvailability,
      operationalAlerts,
      recentActivity
    ] = await Promise.all([
      getDashboardOverview(),
      getTopPerformingMeals(5), // Get top 5 meals for dashboard
      getSubscriptionMetrics(),
      getTodayAvailability(),
      getOperationalAlerts(),
      getRecentActivity(10) // Get latest 10 activities for dashboard
    ]);

    return {
      overview,
      topMeals,
      subscriptionMetrics,
      todayAvailability,
      operationalAlerts,
      recentActivity
    };
  } catch (error) {
    console.error('Error fetching dashboard data:', error);
    throw error;
  }
};

/**
 * Get dashboard data with custom parameters
 * @param {Object} options - Configuration options
 * @param {number} options.topMealsLimit - Limit for top meals
 * @param {number} options.recentActivityLimit - Limit for recent activity
 * @param {number} options.revenueStatsDays - Days for revenue stats
 * @returns {Promise} Object containing dashboard data with custom parameters
 */
export const getCustomDashboardData = async (options = {}) => {
  const {
    topMealsLimit = 10,
    recentActivityLimit = 20,
    revenueStatsDays = 7
  } = options;

  try {
    const [
      overview,
      topMeals,
      subscriptionMetrics,
      todayAvailability,
      operationalAlerts,
      recentActivity,
      revenueStats
    ] = await Promise.all([
      getDashboardOverview(),
      getTopPerformingMeals(topMealsLimit),
      getSubscriptionMetrics(),
      getTodayAvailability(),
      getOperationalAlerts(),
      getRecentActivity(recentActivityLimit),
      getRevenueStats(revenueStatsDays)
    ]);

    return {
      overview,
      topMeals,
      subscriptionMetrics,
      todayAvailability,
      operationalAlerts,
      recentActivity,
      revenueStats
    };
  } catch (error) {
    console.error('Error fetching custom dashboard data:', error);
    throw error;
  }
};

// Export default object for easier imports
const dashboardService = {
  getDashboardOverview,
  getTopPerformingMeals,
  getSubscriptionMetrics,
  getTodayAvailability,
  getOperationalAlerts,
  getRecentActivity,
  getRevenueStats,
  getAllDashboardData,
  getCustomDashboardData
};

export default dashboardService;